import React, { useContext, Fragment, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { Theme, makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  Typography,
  Divider,
  Collapse,
  IconButton,
} from "@material-ui/core";
import { DesignContext } from "../../context/design";
import clsx from "clsx";
import { ExpandMoreRounded, Clear, Done } from "@material-ui/icons";
import { Feature } from "geojson";
import { v4 as uuidv4 } from "uuid";
import EquipDesignAppearance from "./equip-design-appearance";
import EquipDesignWorkfunction from "./equip-design-workfunction";
import EquipDesignComment from "./equip-design-comment";
import EquipDesignImages from "./equip-design-images";
import EquipGenSymbol from "./equip-gen-symbol";
import { LangContext } from "../../context/lang";
import RenderPreviewSymbol from "./render-preview-symbol";
import { scaleTextToZoomLevel } from "../../constant/commons";

const useStyle = makeStyles((theme: Theme) => ({
  equipSetting: {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
  },
  equip: {
    width: theme.spacing(14),
    height: theme.spacing(14),
  },
  flexCenter: {
    display: "flex",
    justifyContent: "center",
  },
  flexMiddle: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  title: {
    fontFamily: ["Montserrat-Medium", "Kanit-Medium"].join(","),
    fontSize: 18,
  },
  divPreviewSymbol: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(6),
    position: "relative",
  },
  radioSize: {
    paddingLeft: theme.spacing(2),
    flexDirection: "row",
  },
  radioDiv: { width: `calc(50% - ${theme.spacing(2)}px)` },
  btn: { textTransform: "unset" },
  gridSave: {
    padding: `0px !important`,
    display: "flex",
    justifyContent: "flex-end",
  },
  successColor: { color: theme.palette.success.main },
}));

const EquipDesignTransformer = () => {
  const classes = useStyle();
  const [appeashow, setappeashow] = useState<boolean>(false);
  const { lang } = useContext(LangContext);
  const { map, event, setevent } = useContext(DesignContext);
  const { feature, designEquips } = event;

  const createNewTransformer = async () => {
    let mapEvent: any = map;
    const draw = mapEvent._controls.find((control: any) => control.modes);
    const { features } = draw.getAll();
    let img = new Image();
    img.width = 100;
    img.height = 100;
    img.src = `data:image/svg+xml,${encodeURIComponent(
      ReactDOMServer.renderToString(
        <EquipGenSymbol property={features[0].properties} />
      )
    )}`;
    Boolean(mapEvent.hasImage(`icon-${features[0].id}`))
      ? await mapEvent.updateImage(`icon-${features[0].id}`, img)
      : await mapEvent.addImage(`icon-${features[0].id}`, img);

    const equipLayer = {
      id: `equip-${features[0].id}`,
      type: "symbol",
      source: {
        type: "geojson",
        data: features[0],
      },
      layout: {
        "icon-image": `icon-${features[0].id}`,
        "icon-size": [
          "interpolate",
          ["exponential", 2],
          ["zoom"],
          10,
          0.01,
          18.384615384615,
          0.35,
        ],
        "icon-allow-overlap": true,
        "icon-rotation-alignment": "map",
        "icon-rotate": features[0].properties.iconRotate
          ? features[0].properties.iconRotate
          : 0,
      },
    };

    const autoAnnoSourceId = uuidv4();
    const autoAnnoSource: Feature = {
      type: "Feature",
      id: autoAnnoSourceId,
      properties: {
        equip_type: "anno-label",
        text: features[0].properties.comment,
        "text-font": "Kanit",
        "text-max-width": 100,
        "text-allow-overlap": true,
        id: autoAnnoSourceId,
        "text-size": [
          "interpolate",
          ["exponential", 2],
          ["zoom"],
          0,
          0,
          scaleTextToZoomLevel(500),
          24,
        ],
        "text-rotate": 0,
        "text-color": "#000000",
      },
      geometry: {
        type: "Point",
        coordinates: [
          features[0].geometry.coordinates[0],
          features[0].geometry.coordinates[1] + 0.00002,
        ],
      },
    };

    const autoAnnoLayer = {
      id: `anno-label-${autoAnnoSourceId}`,
      type: "symbol",
      source: {
        type: "geojson",
        data: autoAnnoSource,
      },
      layout: {
        "text-field": "{text}",
        "text-pitch-alignment": "auto",
        "text-rotation-alignment": "map",
        "text-font": ["Kanit"],
        "text-max-width": 100,
        "text-allow-overlap": true,
        "text-size": [
          "interpolate",
          ["exponential", 2],
          ["zoom"],
          10,
          0,
          scaleTextToZoomLevel(500),
          24,
        ],
        "text-rotate": 0,
      },
      paint: {
        "text-color": "#000000",
      },
    };

    if (Boolean(mapEvent.getLayer(equipLayer.id))) {
      mapEvent.getSource(equipLayer.id).setData(equipLayer.source.data);
      mapEvent.setLayoutProperty(equipLayer.id, "visibility", "visible");
      mapEvent.setLayoutProperty(
        equipLayer.id,
        "icon-rotate",
        features[0].properties.iconRotate
          ? features[0].properties.iconRotate
          : 0
      );
    } else {
      mapEvent.addLayer(equipLayer, `gl-draw-polygon-fill-inactive.cold`);
      features[0].properties.comment &&
        features[0].properties.comment.length > 0 &&
        mapEvent.addLayer(autoAnnoLayer, `gl-draw-polygon-fill-inactive.cold`);
    }
    Boolean(mapEvent.hasImage(`icon-${features[0].id}`)) &&
      mapEvent.updateImage(`icon-${features[0].id}`, img);

    const hasEquip = event.designEquips.findIndex(
      (dE: any) => dE.id === equipLayer.id
    );

    draw.deleteAll();

    setevent({
      ...event,
      type: undefined,
      feature: undefined,
      anno:
        hasEquip < 0 && features[0].properties.comment?.length > 0
          ? event.anno
            ? [...event.anno, autoAnnoLayer]
            : [autoAnnoLayer]
          : event.anno,
      designEquips:
        hasEquip >= 0
          ? event.designEquips.map((eq: any, index: number) => {
              if (index === hasEquip) {
                eq = equipLayer;
              }
              return eq;
            })
          : [...event.designEquips, equipLayer],
    });
  };

  const _cancel = () => {
    const mapEvent: any = map;
    const draw = mapEvent._controls.find((control: any) => control.modes);
    const hasDesignEq = designEquips.find(
      (eq: any) => eq.id === `equip-${feature?.id}`
    );
    if (Boolean(hasDesignEq)) {
      map?.setLayoutProperty(`equip-${feature?.id}`, "visibility", "visible");
    }
    draw.deleteAll();
    setevent({ ...event, type: undefined, feature: undefined });
  };

  return (
    <Fragment>
      <Grid container spacing={2} style={{ width: "100%" }}>
        <Grid item xs={12} className={classes.gridSave}>
          <IconButton size="small" onClick={createNewTransformer}>
            <Done className={classes.successColor} />
          </IconButton>
          <IconButton size="small" onClick={_cancel}>
            <Clear color="error" />
          </IconButton>
        </Grid>
        <Grid item xs={12} className={classes.flexMiddle}>
          <Typography
            variant="subtitle1"
            className={clsx(classes.title, classes.flexCenter)}
          >
            {lang.data.transformer}
          </Typography>
          <div className={clsx(classes.flexCenter, classes.divPreviewSymbol)}>
            {feature && feature.properties && (
              <RenderPreviewSymbol
                feature={feature}
                map={map}
                event={event}
                setevent={setevent}
              />
            )}
          </div>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
      </Grid>
      <div className={classes.equipSetting}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="subtitle1"
                style={{ marginTop: 8, marginBottom: 8 }}
              >
                {lang.data.appearance}
              </Typography>
              <div className={classes.flexMiddle}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setappeashow(!appeashow);
                  }}
                >
                  <ExpandMoreRounded />
                </IconButton>
              </div>
            </div>
            <Collapse in={appeashow}>
              <EquipDesignAppearance />
            </Collapse>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <EquipDesignWorkfunction />
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <EquipDesignComment />
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <EquipDesignImages />
        </Grid>
      </div>
    </Fragment>
  );
};

export default EquipDesignTransformer;
